import { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

// ============================================================================
// Types
// ============================================================================

interface ToastMessage {
  id: number;
  message: string;
  actionText?: string;
  actionCallback?: () => void;
}

interface ToastEventDetail {
  message: string;
  actionText?: string;
  actionCallback?: () => void;
}

// ============================================================================
// Global function (register immediately)
// ============================================================================

// Create global function immediately so it works before component mounts
if (!(window as any)._disabledFieldToastRegistered) {
  (window as any).showDisabledFieldToast = function (
    message: string,
    actionText?: string,
    actionCallback?: () => void
  ) {
    const event = new CustomEvent('show-disabled-field-toast', {
      detail: { message, actionText, actionCallback },
    });
    window.dispatchEvent(event);
  };
  (window as any)._disabledFieldToastRegistered = true;
}

// ============================================================================
// Component
// ============================================================================

export function DisabledFieldToast() {
  const { theme } = useTheme();

  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const nextIdRef = useRef(0);
  const debounceTimeoutRef = useRef<number | null>(null);

  const dismissToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const showToast = useCallback((message: string, actionText?: string, actionCallback?: () => void) => {
    // Debounce to prevent toast spam from rapid tapping
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Only show one toast at a time, replace existing
    setToasts([]);

    debounceTimeoutRef.current = window.setTimeout(() => {
      const id = nextIdRef.current++;
      const toast: ToastMessage = {
        id,
        message,
        actionText,
        actionCallback,
      };

      setToasts([toast]);

      // Auto-dismiss after 4 seconds
      setTimeout(() => {
        dismissToast(id);
      }, 4000);
    }, 100);
  }, [dismissToast]);

  const handleToastEvent = useCallback((event: Event) => {
    const customEvent = event as CustomEvent<ToastEventDetail>;
    const { message, actionText, actionCallback } = customEvent.detail;
    showToast(message, actionText, actionCallback);
  }, [showToast]);

  // Listen for toast events
  useEffect(() => {
    window.addEventListener('show-disabled-field-toast', handleToastEvent);
    return () => {
      window.removeEventListener('show-disabled-field-toast', handleToastEvent);
    };
  }, [handleToastEvent]);

  const onActionClick = useCallback((toast: ToastMessage) => {
    if (toast.actionCallback) {
      toast.actionCallback();
    }
    dismissToast(toast.id);
  }, [dismissToast]);

  return (
    <div className={`disabled-field-toast-container ${theme}`}>
      <div>
        {toasts.map(toast => (
          <div key={toast.id} className="disabled-field-toast">
            <div className="toast-content">
              <i className="fas fa-info-circle toast-icon"></i>
              <span className="toast-message">{toast.message}</span>
              {toast.actionText && (
                <button
                  type="button"
                  className="toast-action-btn"
                  onClick={() => onActionClick(toast)}
                >
                  {toast.actionText}
                </button>
              )}
              <button
                type="button"
                className="toast-dismiss-btn"
                onClick={() => dismissToast(toast.id)}
                aria-label="Dismiss"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

(window as any).DisabledFieldToastReact = DisabledFieldToast;
